import React, { useEffect, useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import "./AddAgentModal.css";

const AddAgentModal = ({ isOpen, onClose, onSave, campaignOptions = [], agentToEdit = null }) => {
  const isEditMode = Boolean(agentToEdit?.id);
  const schema = useMemo(() => {
    if (isEditMode) {
      return Yup.object().shape({
        name: Yup.string().required("Name is required"),
        email: Yup.string().email("Invalid email").required("Email is required"),
        campaign_id: Yup.string().required("Campaign is required"),
      });
    }

    return Yup.object().shape({
      name: Yup.string().required("Name is required"),
      email: Yup.string().email("Invalid email").required("Email is required"),
      campaign_id: Yup.string().required("Campaign is required"),
      password: Yup.string().min(6, "Minimum 6 characters").required("Password is required"),
      confirm_password: Yup.string()
        .oneOf([Yup.ref("password"), null], "Passwords do not match")
        .required("Confirm password is required"),
    });
  }, [isEditMode]);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      name: "",
      email: "",
      campaign_id: "",
      password: "",
      confirm_password: "",
    },
  });

  useEffect(() => {
    if (!isOpen) return;
    if (isEditMode) {
      reset({
        name: agentToEdit?.name || "",
        email: agentToEdit?.email || "",
        campaign_id: String(agentToEdit?.campaign_id || ""),
        password: "",
        confirm_password: "",
      });
      return;
    }

    reset({
      name: "",
      email: "",
      campaign_id: "",
      password: "",
      confirm_password: "",
    });
  }, [isOpen, isEditMode, agentToEdit, reset]);

  if (!isOpen) return null;

  const onSubmit = async (formData) => {
    const payload = {
      name: formData.name.trim(),
      email: formData.email.trim(),
      campaign_id: formData.campaign_id,
    };

    if (isEditMode) {
      payload.agent_id = agentToEdit.id;
    } else {
      payload.password = formData.password;
      payload.confirm_password = formData.confirm_password;
    }

    const ok = await onSave(payload);

    if (ok) {
      reset();
      onClose();
    }
  };

  return (
    <div className="add-agent-modal-overlay">
      <div className="add-agent-modal">
        <h5>{isEditMode ? "Update Agent" : "Add Agent"}</h5>

        <form id="addAgentForm" onSubmit={handleSubmit(onSubmit)}>
          <div className="add-agent-form-grid">
            <div>
              <label>Name</label>
              <Controller
                name="name"
                control={control}
                render={({ field }) => <input {...field} placeholder="Enter name" />}
              />
              {errors.name && <p className="error-text">{errors.name.message}</p>}
            </div>

            <div>
              <label>Email</label>
              <Controller
                name="email"
                control={control}
                render={({ field }) => (
                  <input
                    {...field}
                    placeholder="Enter email"
                    disabled={isEditMode}
                  />
                )}
              />
              {errors.email && <p className="error-text">{errors.email.message}</p>}
            </div>

            <div>
              <label>Campaign</label>
              <Controller
                name="campaign_id"
                control={control}
                render={({ field }) => (
                  <select {...field}>
                    <option value="">Select campaign</option>
                    {campaignOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                )}
              />
              {errors.campaign_id && <p className="error-text">{errors.campaign_id.message}</p>}
            </div>

            {!isEditMode && (
              <div>
                <label>Password</label>
                <Controller
                  name="password"
                  control={control}
                  render={({ field }) => <input {...field} type="password" placeholder="Enter password" />}
                />
                {errors.password && <p className="error-text">{errors.password.message}</p>}
              </div>
            )}

            {!isEditMode && (
              <div>
                <label>Confirm Password</label>
                <Controller
                  name="confirm_password"
                  control={control}
                  render={({ field }) => (
                    <input {...field} type="password" placeholder="Confirm password" />
                  )}
                />
                {errors.confirm_password && (
                  <p className="error-text">{errors.confirm_password.message}</p>
                )}
              </div>
            )}
          </div>
        </form>

        <div className="add-agent-modal-actions">
          <button className="popup-cancel-btn" onClick={onClose} type="button">
            Cancel
          </button>
          <button className="popup-add-btn" type="submit" form="addAgentForm">
            {isEditMode ? "Update Agent" : "Add Agent"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddAgentModal;
